import {
  FooterSettings,
  MaintenanceRichText,
  ResolvedStoreSettings,
  SeoSettings,
  StoreThemeResponse,
} from '../constants/apiResponses.types';

const getFallback = <T>(value: T | undefined | null, fallbackValue: T): T =>
  value === undefined || value === null || value === '' ? fallbackValue : value;

const resolveFooter = (
  source: Partial<FooterSettings> | undefined,
  fallback: FooterSettings
): FooterSettings => {
  if (!source) return fallback;
  return {
    ...source,
    imageMobile: source.imageMobile ?? fallback.imageMobile,
    imageDesktop: source.imageDesktop ?? fallback.imageDesktop,
    showAppchargeLogo: source.showAppchargeLogo ?? fallback.showAppchargeLogo,
    privacyPolicyUrl: source.privacyPolicyUrl ?? fallback.privacyPolicyUrl,
    termsAndConditionsUrl: source.termsAndConditionsUrl ?? fallback.termsAndConditionsUrl,
    openLinksInNewTab: source.openLinksInNewTab ?? fallback.openLinksInNewTab,
  } as FooterSettings;
};

const resolveSeo = (
  source: SeoSettings | undefined,
  fallback: SeoSettings | undefined
): SeoSettings | undefined => {
  if (!source) return fallback;
  if (!fallback) return source;
  return {
    ...source,
    title: getFallback(source.title, fallback.title),
    description: getFallback(source.description, fallback.description),
    keywords: source.keywords?.length ? source.keywords : fallback.keywords,
    socialShareImageUrl: getFallback(source.socialShareImageUrl, fallback.socialShareImageUrl),
    socialTitle: getFallback(source.socialTitle, fallback.socialTitle),
    socialDescription: getFallback(source.socialDescription, fallback.socialDescription),
  };
};

const resolveMaintenanceRichText = (
  source: MaintenanceRichText | undefined,
  fallback: MaintenanceRichText | undefined
): MaintenanceRichText | undefined => {
  if (!source) return fallback;
  if (!fallback) return source;
  return {
    text: getFallback(source.text, fallback.text),
    fontColor: getFallback(source.fontColor, fallback.fontColor),
  };
};

export const getStoreSettings = ({
  storeSettings,
  general,
  storeScreen,
}: StoreThemeResponse): ResolvedStoreSettings => {
  const emptyState = storeSettings?.emptyState;
  return {
    storeTabName: getFallback(storeSettings?.storeTabName, general.storeTabName),
    themeColor: getFallback(storeSettings?.themeColor, '#FFFFFF'),
    bouncyScrollOn: getFallback(storeSettings?.bouncyScrollOn, general.bouncyScrollOn),
    favicon: getFallback(storeSettings?.favicon, general.favicon),
    loaderAnimation: getFallback(storeSettings?.loaderAnimation, general.loaderAnimation),
    defaultPlayerImage: getFallback(
      storeSettings?.defaultPlayerImage,
      storeScreen.defaultPlayerImage
    ),
    footer: resolveFooter(storeSettings?.footer, general.footer),
    noOffersTitleText: getFallback(emptyState?.noOffersTitleText, storeScreen.noOffersTitleText),
    noOffersMessageText: getFallback(
      emptyState?.noOffersMessageText,
      storeScreen.noOffersMessageText
    ),
    seo: resolveSeo(storeSettings?.seo, storeScreen.seo),
    maintenanceModeRichText: resolveMaintenanceRichText(
      storeSettings?.maintenanceModeRichText,
      general.maintenanceModeRichText
    ),
  };
};
